import React, {useMemo} from 'react';
import styled from 'styled-components';
import {Button, ButtonDropdownField, DropdownList, FlexRow, Icon} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {useReportBuilder} from './ReportBuilderContext';
import {FIXED_COLUMN_IDS, orderedDataColumnIds} from './ReportTable/useReportTableColumns';
import {DEFAULT_FIELD_LABELS} from './ReportTable/fieldLabels';

export interface ColumnManagerProps {
	fieldLabels?: Record<string, string>;
}

/** Merges the definition's canonical leaf-column order with the current `tableState.columnOrder`:
 * current order wins for columns already present (so user reordering persists across renders),
 * any newly-added field path (not yet in `columnOrder`, e.g. after an AI patch) is appended at the
 * end in canonical order. Fixed (`__`-prefixed) columns are excluded — they are never toggleable
 * or reorderable via this control. */
function resolveColumnIds(definitionFieldPaths: string[], currentOrder: string[]): string[] {
	const currentDataIds = currentOrder.filter(id => !id.startsWith('__'));
	const currentSet = new Set(currentDataIds);
	const missing = definitionFieldPaths.filter(id => !currentSet.has(id));
	return [...currentDataIds, ...missing];
}

/** DS DropdownList renders its root as a flex:1 item, which grows to fill the controls row and
 * left-aligns the trigger (defeating the row's justify-content:flex-end). Wrap it in a hugging
 * inline-flex container (flex:0 0 auto) so the Columns button stays content-width and the Group By
 * + Columns cluster sits at the right, as in the design. (GroupByControl gets this from its FlexColumn.) */
const ColumnsTriggerWrapper = styled.div`
	display: inline-flex;
	flex: 0 0 auto;
`;

/** Columns dropdown: one checkbox row per leaf data column (visibility toggle) plus up/down
 * buttons to reorder adjacent columns. Fixed structural columns (expander/actions) are never
 * listed. Operates purely through `useReportBuilder()`'s `tableState`/`updateTableState`. */
export function ColumnManager({fieldLabels}: ColumnManagerProps): JSX.Element {
	const {formatMessage} = useIntl();
	const {definition, tableState, updateTableState} = useReportBuilder();

	const labels = useMemo(() => ({...DEFAULT_FIELD_LABELS, ...fieldLabels}), [fieldLabels]);

	const columnIds = useMemo(
		() => resolveColumnIds(orderedDataColumnIds(definition), tableState.columnOrder),
		[definition, tableState.columnOrder]
	);

	const toggleVisibility = (columnId: string): void => {
		const isVisible = tableState.columnVisibility[columnId] !== false;
		updateTableState({
			columnVisibility: {...tableState.columnVisibility, [columnId]: !isVisible},
		});
	};

	/** Swaps `columnId` with its adjacent data column in the given direction. Operates on
	 * `columnIds` (the fully resolved id list — same resolution used for rendering, so it always
	 * contains every definition field even if `tableState.columnOrder` is stale/missing one) rather
	 * than on `tableState.columnOrder` directly, so a field present in the definition but not yet
	 * materialized into `columnOrder` is never dropped by the swap. Fixed columns are re-spliced
	 * back into their original absolute positions (leading expander, trailing actions) around the
	 * resolved+swapped data segment. */
	const moveColumn = (columnId: string, direction: -1 | 1): void => {
		const index = columnIds.indexOf(columnId);
		const targetIndex = index + direction;
		if (index === -1 || targetIndex < 0 || targetIndex >= columnIds.length) {
			return;
		}
		const nextDataOrder = [...columnIds];
		[nextDataOrder[index], nextDataOrder[targetIndex]] = [nextDataOrder[targetIndex], nextDataOrder[index]];

		// Fixed columns keep their existing absolute positions (leading expander, trailing
		// actions); the full resolved+swapped data segment (which may be longer than the data
		// portion of the stale columnOrder) is spliced in wherever data ids currently are.
		const nextOrder: string[] = [];
		let dataConsumed = false;
		for (const id of tableState.columnOrder) {
			if (FIXED_COLUMN_IDS.includes(id)) {
				nextOrder.push(id);
			} else if (!dataConsumed) {
				nextOrder.push(...nextDataOrder);
				dataConsumed = true;
			}
		}
		if (!dataConsumed) {
			nextOrder.push(...nextDataOrder);
		}
		updateTableState({columnOrder: nextOrder});
	};

	return (
		<ColumnsTriggerWrapper>
			<DropdownList>
				<DropdownList.Trigger>
					{/* the design: Columns is the neutral field-style control (grey border #6b6d7b,
				    dark #232334 text, leading list glyph + built-in trailing chevron) — the same
				    "Filter" component as Download, NOT a brand-blue ButtonDropdown. aria-label carries
				    the accessible name since the label sits inside a FlexRow rather than as text. */}
					<ButtonDropdownField
						width={'fit-content'}
						aria-label={formatMessage({id: 'ai_report_builder.columns'})}
						data-cy={'ai-report-column-manager'}
					>
						<FlexRow alignItems={'center'} gap={'xs'}>
							<Icon icon={'list'} newColor={'onlight'} size={'s'} />
							<FormattedMessage id={'ai_report_builder.columns'} />
						</FlexRow>
					</ButtonDropdownField>
				</DropdownList.Trigger>
				<DropdownList.Content offset={4}>
					{columnIds.map((columnId, index) => (
						<DropdownList.CheckboxItem
							key={columnId}
							checked={tableState.columnVisibility[columnId] !== false}
							onChange={() => toggleVisibility(columnId)}
							data-cy={`ai-report-column-toggle-${columnId}`}
						>
							<FlexRow justifyContent={'space-between'} alignItems={'center'}>
								<span>{labels[columnId] ?? columnId}</span>
								<FlexRow>
									<Button
										iconOnly
										emphasis="low"
										icon="chevronUp"
										disabled={index === 0}
										aria-label={formatMessage({id: 'ai_report_builder.move_column_up'})}
										data-cy={`ai-report-column-up-${columnId}`}
										onPress={() => moveColumn(columnId, -1)}
									/>
									<Button
										iconOnly
										emphasis="low"
										icon="chevronDown"
										disabled={index === columnIds.length - 1}
										aria-label={formatMessage({id: 'ai_report_builder.move_column_down'})}
										data-cy={`ai-report-column-down-${columnId}`}
										onPress={() => moveColumn(columnId, 1)}
									/>
								</FlexRow>
							</FlexRow>
						</DropdownList.CheckboxItem>
					))}
				</DropdownList.Content>
			</DropdownList>
		</ColumnsTriggerWrapper>
	);
}
